import { useState, useEffect, useMemo } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getTrainingRenewals, GetTrainingRenewalsOutputType } from 'zite-endpoints-sdk';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Clock, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Renewal = GetTrainingRenewalsOutputType['renewals'][0];

const statusColor: Record<string, string> = {
  overdue: 'bg-destructive/15 text-destructive border-destructive/30',
  due_soon: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  current: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};

const statusLabel: Record<string, string> = {
  overdue: 'Overdue',
  due_soon: 'Due Soon',
  current: 'Current',
};

export default function TrainingRenewals() {
  const { user } = useAuth();
  const isManager = ['Manager', 'Admin', 'Owner'].includes(user?.role || '');
  const [data, setData] = useState<GetTrainingRenewalsOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scope, setScope] = useState<'own' | 'team'>(isManager ? 'team' : 'own');

  const load = () => {
    setLoading(true);
    getTrainingRenewals({ scope }).then(r => { setData(r); setLoading(false); });
  };

  useEffect(() => { load(); }, [scope]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.renewals.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return r.itemName.toLowerCase().includes(q) || r.staffName.toLowerCase().includes(q);
      }
      return true;
    });
  }, [data, search, statusFilter]);

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Training Renewals</h1>
          <p className="text-sm text-muted-foreground mt-1">Expiry dates, refresher intervals, and renewal status</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Button>
      </div>

      {!loading && data && (
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-destructive/30 rounded-lg p-3 bg-destructive/5">
            <div className="text-2xl font-bold text-destructive">{data.summary.overdue}</div>
            <div className="text-xs text-muted-foreground">Overdue</div>
          </div>
          <div className="border border-amber-500/30 rounded-lg p-3 bg-amber-500/5">
            <div className="text-2xl font-bold text-amber-400">{data.summary.dueSoon}</div>
            <div className="text-xs text-muted-foreground">Due Soon (30d)</div>
          </div>
          <div className="border border-emerald-500/30 rounded-lg p-3 bg-emerald-500/5">
            <div className="text-2xl font-bold text-emerald-400">{data.summary.current}</div>
            <div className="text-xs text-muted-foreground">Current</div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search training or staff..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="due_soon">Due Soon</SelectItem>
            <SelectItem value="current">Current</SelectItem>
          </SelectContent>
        </Select>
        {isManager && (
          <Select value={scope} onValueChange={v => setScope(v as 'own' | 'team')}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="own">My Own</SelectItem>
              <SelectItem value="team">Team</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
          <p className="font-medium">No training renewals {statusFilter !== 'all' ? `with status "${statusLabel[statusFilter]}"` : 'due'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r, i) => (
            <div key={i} className="border border-border rounded-lg p-3 bg-card flex items-center gap-3">
              {r.status === 'overdue' ? <AlertTriangle className="w-5 h-5 text-destructive shrink-0" /> :
               r.status === 'due_soon' ? <Clock className="w-5 h-5 text-amber-400 shrink-0" /> :
               <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{r.itemName}</p>
                <p className="text-xs text-muted-foreground">{r.staffName} · {r.type === 'module' ? 'Module' : 'Certificate'} · {r.refresherIntervalDays ? `${r.refresherIntervalDays}d interval` : ''}</p>
              </div>
              <div className="text-right shrink-0">
                <Badge variant="outline" className={`text-[10px] ${statusColor[r.status]}`}>{statusLabel[r.status]}</Badge>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {r.nextRenewalDate ? `Due: ${new Date(r.nextRenewalDate).toLocaleDateString('en-GB')}` : ''}
                  {r.daysUntilDue < 0 ? ` (${Math.abs(r.daysUntilDue)}d overdue)` : r.daysUntilDue <= 30 ? ` (${r.daysUntilDue}d)` : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
